import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDebounce } from "use-debounce";
import { Content } from "../components";
import TableSkeleton from "../Skeletons/TableSkeleton";
import AddEditModal, {
  AddEditModalState,
  IaddEditModalStates,
} from "../pageComponents/AssociateClinic/AddEditModal";
import {
  CustomTableRow,
  Table,
  TableBody,
  TableHead,
  TableNoContent,
  TableRowTitle,
  TableStatusWithToggle,
} from "../components/Table";
import { TableButtons } from "../components/NewTable";
import Pagination from "../components/Pagination";
import DeleteModal, {
  DeleteModalState,
  IdeleteModalStates,
} from "../pageComponents/AssociateClinic/DeleteModal";
import ViewProviderModal from "../pageComponents/AssociateClinic/ViewProviderModal";
import { loginSliceValue } from "../redux/reducers/loginSlice";
import { AssociateClinicTableHeadData } from "../hooks/associateClinic/staticData";

import {
  useAddAssociateClinic,
  useGetAllRTMPlan,
  useFetchAssociatedClinic,
  useToggleStatus,
} from "../hooks/associateClinic/index";

import {
  IAddAssociateClinicNewReq,
  IAssociateClinic,
} from "../hooks/associateClinic/index.type";

const AssociateClinic = () => {
  const { clinicId } = useParams();
  const { userDetails } = useSelector(loginSliceValue);

  let loginUserRole = userDetails.data?.user.role;
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const perpageData = 10;
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  // ====================================== Add edit states start ======================================
  const [addEditModal, setAddEditModal] = React.useState<AddEditModalState>({
    status: false,
  });
  const startAddEditModalStatesValue: IaddEditModalStates = {
    name: { value: "", error: "" },
    contactName: { value: "", error: "" },
    email: { value: "", error: "" },
    phone: { value: "", error: "" },
    billingProvider: { value: [], error: "" },
    address: { value: "", error: "" },
  };
  const [addEditModalStates, setAddEditModalStates] =
    React.useState<IaddEditModalStates>(startAddEditModalStatesValue);
  // ====================================== Add edit states end ======================================

  // ====================================== Toggle states start ======================================
  const [viewProviderModal, setViewProviderModal] =
    React.useState<DeleteModalState>({
      status: false,
    });
  // ====================================== Toggle states end ======================================

  // ====================================== Delete states start ======================================
  const [deleteModal, setDeleteModal] = React.useState<DeleteModalState>({
    status: false,
  });

  const startDeleteModalStatesValue = {
    name: { value: "", error: "" },
  };
  const [deleteModalStates, setDeleteModalStates] =
    React.useState<IdeleteModalStates>(startDeleteModalStatesValue);

  useEffect(() => {
    getAssociatedClinic();
  }, [pageNumber, debouncedSearchTerm]);

  // ====================================== Delete states end ======================================

  // ====================================== Hooks ======================================
  // const {
  //   AssociateClinic,
  //   getAssociateClinicData,
  //   AssociateClinicDataLoading,
  // } = useGetAllRTMPlan({
  //   pageNumber,
  //   perpageData,
  //   searchTerm: debouncedSearchTerm,
  //   clinicId: clinicId || "",
  // });

  const { associatedClinic, associatedClinicLoading, getAssociatedClinic } =
    useFetchAssociatedClinic({
      pageNumber: pageNumber,
      searchTerm: searchTerm,
      clinicId: clinicId
    });

  const { addAssociateClinicLoading, addAssociateClinic } =
    useAddAssociateClinic({
      clinicAssociateId: clinicId,
      isUpdate: addEditModal.data?._id || "",
    });
  const { toggleClinicStatus } = useToggleStatus();

  // const { deleteAssociateClinicLoading, deleteAssociateClinic } = useDeleteAssociateClinic({
  //   clinicAssociateId: deleteModal.data?._id
  // });

  // const { toggleAssociateClinicLoading, toggleAssociateClinic } = useToggleAssociateClinic({
  //   clinicAssociateId: toggleModal.data?._id
  // });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // pagination
  const onPageChange = (currentPage: { selected: number }) => {
    setPageNumber(currentPage.selected + 1);
  };

  // ====================================== Add edit functions start ======================================
  React.useEffect(() => {
    if (addEditModal.data?._id) {
      let billingProviders: string[] = [];

      if (Array.isArray(addEditModal.data.billingProvider)) {
        billingProviders = addEditModal.data.billingProvider
          .map((item) => {
            if (typeof item === "object" && item !== null) {
              return item.name || "";
            } else if (typeof item === "string") {
              return item;
            }
            return "";
          })
          .filter((item) => item !== "");
      } else if (typeof addEditModal.data.billingProvider === "string") {
        billingProviders = [addEditModal.data.billingProvider];
      }
      setAddEditModalStates({
        name: { value: addEditModal.data.name || "", error: "" },
        contactName: { value: addEditModal.data.contactName || "", error: "" },
        email: { value: addEditModal.data.email || "", error: "" },
        phone: { value: addEditModal.data.phone || "", error: "" },
        billingProvider: {
          value: billingProviders,
          error: "",
        },
        address: { value: addEditModal.data.address || "", error: "" },
      });
    }
  }, [addEditModal]);

  const handleCloseModal = () => {
    setAddEditModal({ status: false });
    setAddEditModalStates(startAddEditModalStatesValue);
  };

  const handleAddAssociateClinicAPI = async (
    formData: IAddAssociateClinicNewReq
  ) => {
    const response = await addAssociateClinic(formData);
    if (response?.status === "success") {
      handleCloseModal();
      getAssociatedClinic();
    }
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return /^\d{10}$/.test(digitsOnly);
  };
  
  const unMaskPhone = (phone: string) => {
    return phone.replace(/\D/g, '');
  };

  const handleAddAssociateClinic = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    let isValid = true;

    // Trim values before validation
    const trimmedName = addEditModalStates.name.value.trim();
    const trimmedContactName = addEditModalStates.contactName.value.trim();
    const trimmedEmail = addEditModalStates.email.value.trim();
    const trimmedPhone = addEditModalStates.phone.value.trim();
    const trimmedAddress = addEditModalStates.address.value.trim();

    // Validate name
    if (!trimmedName) {
      setAddEditModalStates((prevVal) => ({
        ...prevVal,
        name: { ...prevVal.name, error: "Please enter clinic name" },
      }));
      isValid = false;
    }

    // Validate email
    if (!trimmedEmail) {
      setAddEditModalStates((prevVal) => ({
        ...prevVal,
        email: { ...prevVal.email, error: "Please enter email address" },
      }));
      isValid = false;
    } else if (!validateEmail(trimmedEmail)) {
      setAddEditModalStates((prevVal) => ({
        ...prevVal,
        email: {
          ...prevVal.email,
          error: "Please enter a valid email address",
        },
      }));
      isValid = false;
    }

    // Validate phone
    if (!trimmedPhone) {
      setAddEditModalStates((prevVal) => ({
        ...prevVal,
        phone: { ...prevVal.phone, error: "Please enter phone number" },
      }));
      isValid = false;
    } else {
      // Unmask the phone number before validation
      const unmaskedPhone = unMaskPhone(trimmedPhone);
      if (!validatePhone(unmaskedPhone)) {
        setAddEditModalStates((prevVal) => ({
          ...prevVal,
          phone: {
            ...prevVal.phone,
            error: "Please enter a valid 10-digit phone number",
          },
        }));
        isValid = false;
      } else {
        // Update the state with the unmasked phone number
        setAddEditModalStates((prevVal) => ({
          ...prevVal,
          phone: { ...prevVal.phone, value: unmaskedPhone, error: "" },
        }));
      }
    }

    // Validate address
    if (!trimmedAddress) {
      setAddEditModalStates((prevVal) => ({
        ...prevVal,
        address: { ...prevVal.address, error: "Please enter address" },
      }));
      isValid = false;
    }

    if (
      !addEditModalStates.billingProvider.value ||
      addEditModalStates.billingProvider.value.length === 0
    ) {
      setAddEditModalStates((prevVal) => ({
        ...prevVal,
        billingProvider: {
          ...prevVal.billingProvider,
          error: "Please select at least one billing provider",
        },
      }));
      isValid = false;
    }

    if (isValid) {
      const formData: IAddAssociateClinicNewReq = {
        name: trimmedName,
        email: trimmedEmail,
        // Ensure phone number is sent as digits only
        phone: unMaskPhone(trimmedPhone),
        address: trimmedAddress,
        billingProvider: addEditModalStates.billingProvider.value,
        ...(trimmedContactName && { contactName: trimmedContactName }), // Only include if not empty
      };

      handleAddAssociateClinicAPI(formData);
    }
  };
  // ====================================== Add edit functions end ======================================

  // ====================================== Toggle functions start ======================================
  // const handleToggleAssociateClinic = async (event: React.FormEvent<HTMLFormElement>) => {
  //   event.preventDefault();

  //   if (toggleModal.data) {
  //     const data = await toggleAssociateClinic({ isActive: !toggleModal.data.isActive });
  //     if (data?.status === 'success') {
  //       setToggleModal({ status: false });
  //       getAssociateClinicData();
  //     }
  //   }
  // };
  // ====================================== Toggle functions end ======================================

  // ====================================== Delete functions start ======================================
  const handleCloseDeleteModal = () => {
    setDeleteModal({ status: false });
    setDeleteModalStates(startDeleteModalStatesValue);
  };

  // const handleDeleteAssociateClinic = async (event: React.FormEvent<HTMLFormElement>) => {
  //   event.preventDefault();

  //   if (deleteModal.data) {
  //     if (!deleteModalStates.name.value.trim()) {
  //       setDeleteModalStates(prevVal => ({
  //         ...prevVal,
  //         name: { ...prevVal.name, error: 'Please enter valid name' }
  //       }));
  //     } else if (deleteModalStates.name.value !== deleteModal.data.name) {
  //       setDeleteModalStates(prevVal => ({
  //         ...prevVal,
  //         name: { ...prevVal.name, error: 'Name does not match' }
  //       }));
  //     } else {
  //       const data = await deleteAssociateClinic();
  //       if (data?.status === 'success') {
  //         handleCloseDeleteModal();
  //         getAssociateClinicData();
  //       }
  //     }
  //   } else {
  //     setDeleteModalStates(prevVal => ({
  //       ...prevVal,
  //       name: { ...prevVal.name, error: 'Please select clinic to delete' }
  //     }));
  //   }
  // };
  // ====================================== Delete functions end ======================================
  const handleToggleClinicStatus = async (
    associatedClinic: IAssociateClinic
  ) => {
    const updatedStatus = !associatedClinic.active;

    try {
      const data = await toggleClinicStatus({
        clinicId: associatedClinic._id,
        active: updatedStatus,
      });

      if (data?.status === 201) {
        getAssociatedClinic();
      }
    } catch (error) {
      console.error("Error toggling clinic status:", error);
    }
  };

  return (
    <Content
      currentMenu="Associate-clinics"
      headerTitle="Associated Clinics"
      addBtn={true}
      addBtnClick={() => setAddEditModal({ status: true })}
      addBtnTitle="Add Associated Clinic"
      searchBox={true}
      searchPlaceholder="Search Associated Clinic"
      handleSearchChange={handleSearchChange}
      totalReult={
        associatedClinic && associatedClinic.totalResults > 0
          ? associatedClinic.totalResults + " Associated Clinics"
          : ""
      }
    >
      <Table>
        <TableHead data={AssociateClinicTableHeadData} />
        <TableBody>
          <CustomTableRow>
            {associatedClinicLoading ? (
              <TableSkeleton
                rows={AssociateClinicTableHeadData.length}
                columns={5}
              />
            ) : associatedClinic && associatedClinic.totalResults > 0 ? (
              associatedClinic.data.data.map((clinic: IAssociateClinic) => (
                <tr key={clinic._id}>
                  <td></td>
                  <TableRowTitle>{clinic.name}</TableRowTitle>
                  <td>{clinic.email}</td>
                  <td>{clinic.phone}</td>
                  <td>{clinic.address}</td>
                  <td
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <p style={{ margin: "1rem", height: "100%" }}>
                      {clinic.billingProvider.length}
                    </p>
                    {clinic.billingProvider.length >= 1 && (
                      <TableButtons
                        customStyle={{
                          border: "none",
                          // alignItems: "center",
                          paddingTop: "1rem",
                          height: "100%",
                        }}
                        buttons={[
                          {
                            icon: "new-eye-icon",
                            tooltip: "View Billing Provider",
                            onClick: () =>
                              setViewProviderModal({
                                status: true,
                                data: clinic,
                              }),
                          },
                        ]}
                      />
                    )}
                  </td>

                  <td>
                    <TableStatusWithToggle
                      id={clinic._id}
                      checked={clinic.active}
                      onChange={() => handleToggleClinicStatus(clinic)}
                    />
                  </td>
                  <TableButtons
                    buttons={[
                      {
                        icon: "edit-3",
                        tooltip: "Edit Associate Clinic",
                        onClick: () =>
                          setAddEditModal({ status: true, data: clinic }),
                      },
                      {
                        icon: "trash",
                        tooltip: "Delete Associate Clinic",
                        variant: "danger",
                        onClick: () =>
                          setDeleteModal({ status: true, data: clinic }),
                      },
                    ]}
                  />
                  <td></td>
                </tr>
              ))
            ) : (
              <TableNoContent
                colSpan={AssociateClinicTableHeadData.length}
                message={
                  searchTerm
                    ? "No Associate Clinics found matching your search"
                    : "No Associate Clinics found"
                }
              />
            )}
          </CustomTableRow>
        </TableBody>
      </Table>

      <Pagination
        itemsPerPage={10}
        totalItems={associatedClinic ? associatedClinic.totalResults : 0}
        onPageChange={onPageChange}
      />

      {addEditModal.status && (
        <AddEditModal
          loading={addAssociateClinicLoading}
          addEditModal={addEditModal}
          onCloseModal={handleCloseModal}
          addEditModalStates={addEditModalStates}
          setAddEditModalStates={setAddEditModalStates}
          handleAddEdit={handleAddAssociateClinic}
        />
      )}
      {viewProviderModal.status && (
        <ViewProviderModal
          loading={associatedClinicLoading}
          toggleModal={viewProviderModal}
          onCloseModal={() => setViewProviderModal({ status: false })}
        />
      )}

      {/* {deleteModal.status && (
        <DeleteModal
          loading={deleteAssociateClinicLoading}
          deleteModal={deleteModal}
          deleteModalStates={deleteModalStates}
          setDeleteModalStates={setDeleteModalStates}
          onCloseModal={handleCloseDeleteModal}
          handleDelete={handleDeleteAssociateClinic}
        />
      )} */}
    </Content>
  );
};

export default AssociateClinic;
